import { mealTimingLabel } from "./format";
import type { EncounterPrescriptionItem } from "./types/encounter";

/** Buổi uống, theo đúng thứ tự trong ngày. */
const SLOTS = [
  ["doseMorning", "Sáng"],
  ["doseNoon", "Trưa"],
  ["doseAfternoon", "Chiều"],
  ["doseEvening", "Tối"],
] as const;

type DoseFields = Pick<
  EncounterPrescriptionItem,
  "doseMorning" | "doseNoon" | "doseAfternoon" | "doseEvening" | "mealTiming" | "note"
>;

/**
 * Cách dùng một dòng thuốc, viết cho BỆNH NHÂN đọc — "Sáng 1 – Tối 1, sau bữa ăn".
 *
 * <p>Một chỗ duy nhất vì trước đây có bốn (#19165). Phiếu tóm tắt lần khám tự rút gọn thành
 * "S 1 · T 1" và **bỏ hẳn chỉ dẫn bữa ăn** — phiếu đó giao tận tay bệnh nhân, nên mất "sau ăn" là
 * mất thông tin dùng thuốc, còn "T" thì không rõ là trưa hay tối.
 *
 * <p><b>Không kèm số lượng.</b> Phiếu in đã có cột SL riêng; nơi nào cần số lượng thì tự nối thêm.
 *
 * <p>{@code ANYTIME} cố ý không in: "bất kỳ lúc nào" không phải một chỉ dẫn, in ra chỉ làm loãng
 * dòng chữ mà bệnh nhân cần đọc.
 */
export function describeDose(it: DoseFields): string {
  const slots = SLOTS.map(([key, label]) => (it[key] ? `${label} ${it[key]}` : null))
    .filter(Boolean)
    .join(" – ");
  const meal =
    it.mealTiming && it.mealTiming !== "ANYTIME"
      ? mealTimingLabel[it.mealTiming].toLowerCase()
      : "";
  const schedule = [slots, meal].filter(Boolean).join(", ");
  return [schedule, it.note?.trim()].filter(Boolean).join(" · ") || "Chưa nhập cách dùng";
}

/**
 * Số ngày uống, SUY RA từ số lượng — {@code quantity / liều mỗi ngày} (#19165).
 *
 * <p>Số ngày **không được lưu**: bảng {@code encounter_prescription_items} không có cột nào cho nó.
 * Ô "Số ngày" ở màn kê đơn chỉ là máy tính bỏ túi — nó nhân ra {@code quantity} rồi biến mất. Đây
 * đúng là phép suy ngược mà màn khám đang dùng để hiện lại con số đó, nên phiếu in và màn hình luôn
 * nói cùng một điều.
 *
 * <p>Hệ quả cần biết khi kiểm thử: số lượng lẻ thì số ngày bị **làm tròn** (13 viên, 2 lần/ngày →
 * "7 ngày"). Muốn con số chính xác thì phải lưu nó thật — xem ghi chú ở #19165.
 *
 * @return số ngày, hoặc null khi không suy ra được (không có liều theo buổi, hoặc chưa nhập SL).
 */
export function doseDurationDays(
  it: Pick<
    EncounterPrescriptionItem,
    "doseMorning" | "doseNoon" | "doseAfternoon" | "doseEvening" | "quantity"
  >,
): number | null {
  const perDay = it.doseMorning + it.doseNoon + it.doseAfternoon + it.doseEvening;
  if (perDay <= 0 || !it.quantity || it.quantity <= 0) return null;
  return Math.max(1, Math.round(it.quantity / perDay));
}
